import { createLogger } from '@/shared/logging/logger'
import type {
  AssistantArtifact,
  AssistantJob,
  AssistantJobLogEntry,
  AssistantPlan,
  AssistantStep,
  AssistantStepType,
} from './types'

const logger = createLogger('AssistantOrchestrator')

export interface AssistantStepExecutionResult<TContext> {
  status?: 'done' | 'skipped'
  contextPatch?: Partial<TContext>
  artifacts?: AssistantArtifact[]
  logMessage?: string
}

export interface AssistantStepExecutionContext<TContext> {
  plan: AssistantPlan<TContext>
  job: AssistantJob<TContext>
  step: AssistantStep
  signal?: AbortSignal
  reportProgress: (update: { progressPercent?: number; progressLabel?: string | null }) => void
}

export type AssistantStepExecutor<TContext> = (
  context: AssistantStepExecutionContext<TContext>,
) => Promise<AssistantStepExecutionResult<TContext>>

export type AssistantStepExecutorMap<TContext> = Partial<
  Record<AssistantStepType, AssistantStepExecutor<TContext>>
>

export interface ExecuteAssistantPlanOptions<TContext> {
  executors: AssistantStepExecutorMap<TContext>
  signal?: AbortSignal
  onJobUpdate?: (job: AssistantJob<TContext>) => void
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

function createLogEntry(
  message: string,
  level: 'info' | 'error',
  stepId?: string,
): AssistantJobLogEntry {
  return {
    id: crypto.randomUUID(),
    level,
    message,
    stepId,
    createdAt: Date.now(),
  }
}

function cloneSteps(steps: readonly AssistantStep[]): AssistantStep[] {
  return steps.map((step) => ({ ...step }))
}

function cloneArtifacts(artifacts: readonly AssistantArtifact[]): AssistantArtifact[] {
  return artifacts.map((artifact) => ({
    ...artifact,
    data: artifact.data ? { ...artifact.data } : undefined,
  }))
}

function cloneJob<TContext>(job: AssistantJob<TContext>): AssistantJob<TContext> {
  return {
    ...job,
    context: { ...(job.context as Record<string, unknown>) } as TContext,
    steps: cloneSteps(job.steps),
    artifacts: cloneArtifacts(job.artifacts),
    logs: job.logs.map((entry) => ({ ...entry })),
  }
}

export async function executeAssistantPlan<TContext extends object>(
  plan: AssistantPlan<TContext>,
  options: ExecuteAssistantPlanOptions<TContext>,
): Promise<AssistantJob<TContext>> {
  const job: AssistantJob<TContext> = {
    id: crypto.randomUUID(),
    planId: plan.id,
    status: 'running',
    currentStepId: null,
    startedAt: Date.now(),
    finishedAt: null,
    context: { ...plan.context },
    steps: cloneSteps(plan.steps),
    artifacts: [],
    logs: [createLogEntry('Assistant job started', 'info')],
  }

  const emit = () => {
    options.onJobUpdate?.(cloneJob(job))
  }

  emit()

  for (const step of job.steps) {
    if (options.signal?.aborted) {
      job.status = 'cancelled'
      job.currentStepId = null
      job.finishedAt = Date.now()
      job.logs.push(createLogEntry('Assistant job cancelled', 'info', step.id))
      emit()
      return job
    }

    const executor = options.executors[step.type]
    if (!executor) {
      step.status = 'failed'
      step.errorMessage = `Missing executor for ${step.type}`
      job.status = 'failed'
      job.currentStepId = step.id
      job.finishedAt = Date.now()
      job.logs.push(createLogEntry(step.errorMessage, 'error', step.id))
      emit()
      return job
    }

    job.currentStepId = step.id
    step.status = 'running'
    step.progressPercent = 0
    step.progressLabel = null
    job.logs.push(createLogEntry(`Running step: ${step.title}`, 'info', step.id))
    emit()

    const reportProgress: AssistantStepExecutionContext<TContext>['reportProgress'] = ({
      progressPercent,
      progressLabel,
    }) => {
      if (typeof progressPercent === 'number' && Number.isFinite(progressPercent)) {
        step.progressPercent = Math.max(0, Math.min(100, progressPercent))
      }
      if (progressLabel !== undefined) {
        step.progressLabel = progressLabel
      }
      emit()
    }

    try {
      const result = await executor({
        plan,
        job,
        step,
        signal: options.signal,
        reportProgress,
      })

      step.status = result.status ?? 'done'
      step.progressPercent = step.status === 'done' ? 100 : step.progressPercent
      if (result.contextPatch) {
        job.context = {
          ...job.context,
          ...result.contextPatch,
        }
      }
      if (result.artifacts?.length) {
        job.artifacts.push(...result.artifacts)
      }
      if (result.logMessage) {
        job.logs.push(createLogEntry(result.logMessage, 'info', step.id))
      }
      emit()
    } catch (error) {
      if (options.signal?.aborted || isAbortError(error)) {
        step.status = 'skipped'
        job.status = 'cancelled'
        job.currentStepId = null
        job.finishedAt = Date.now()
        job.logs.push(createLogEntry('Assistant job cancelled', 'info', step.id))
        emit()
        return job
      }

      const message = error instanceof Error ? error.message : String(error)
      logger.error('Assistant step failed', { stepType: step.type, message })
      step.status = 'failed'
      step.errorMessage = message
      job.status = 'failed'
      job.finishedAt = Date.now()
      job.logs.push(createLogEntry(message, 'error', step.id))
      emit()
      return job
    }
  }

  job.status = 'done'
  job.currentStepId = null
  job.finishedAt = Date.now()
  job.logs.push(createLogEntry('Assistant job finished', 'info'))
  emit()
  return job
}
